import { ExternalLink } from 'lucide-react';
import NextLink from 'next/link';
import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';

import { Trigger } from '../../domain';
import { ArgumentDisplay } from './components/ArgumentDisplay';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export function DuneQueryTriggerFullDetails({ trigger }: Props) {
  const queryId = useMemo(() => trigger.context?.duneQueryId, [trigger]);
  const queryUrl = useMemo(() => (queryId ? `https://dune.com/queries/${queryId}` : undefined), [queryId]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!trigger.currencyVolumeExpression) return '-';
    return <CurrencyDisplay value={trigger.currencyVolumeExpression} isDuneColumn />;
  }, [trigger.currencyVolumeExpression]);

  const userArgumentDisplay = useMemo(() => {
    return <ArgumentDisplay value="user_address" suffix="column" />;
  }, []);

  const volumeArgumentDisplay = useMemo(() => {
    return <ArgumentDisplay value="quantity" suffix="column" />;
  }, []);

  const networkDisplay = useMemo(() => {
    return <ArgumentDisplay value="chain_id" suffix="column" />;
  }, []);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow
        label="Executed with"
        value={
          <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
            <Text as="span" variant="body-sm" color="muted">
              Snapshots of Dune Query
            </Text>
            {queryId && queryUrl && (
              <NextLink href={queryUrl} target="_blank" className="tw:flex tw:gap-0.5 tw:no-underline">
                <Badge size="sm">
                  <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center">
                    <span>{queryId}</span>
                    <ExternalLink size={12} />
                  </div>
                </Badge>
              </NextLink>
            )}
          </div>
        }
      />
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="User argument" value={userArgumentDisplay} />
      <KeyValueRow label="Volume argument calculation" value={volumeArgumentDisplay} />
    </KeyValueTable>
  );
}
